/**
 * Logique PURE de la page « Mes fichiers » et des listes de documents :
 * libellés, regroupement par contexte, filtrage, droit de suppression.
 * Aucun accès réseau — tout est testé dans `list-helpers.test.ts`.
 */

import { formatFileSize, type DocumentRow, type DocumentScope } from '@jeraisonne/documents';
import { matchesQuery } from '@/lib/contact-search';

export type DocumentScopeFilter = 'all' | DocumentScope;

export const DOCUMENT_SCOPE_FILTERS: ReadonlyArray<{ value: DocumentScopeFilter; label: string }> =
  [
    { value: 'all', label: 'Tous' },
    { value: 'contact', label: 'Contacts' },
    { value: 'class', label: 'Classes' },
    { value: 'room', label: 'Réunions' },
    { value: 'discussion', label: 'Discussions' },
  ];

export const DOCUMENT_SCOPE_LABELS: Record<DocumentScope, string> = {
  contact: 'Contact',
  class: 'Classe',
  room: 'Réunion',
  discussion: 'Discussion',
};

/** L'AUTRE personne d'un fichier de contact (celui qui n'est pas moi). */
export function otherPartyId(doc: DocumentRow, userId: string): string | null {
  if (doc.scope !== 'contact') return null;
  return doc.uploadedBy === userId ? doc.peerId : doc.uploadedBy;
}

/** Clé de regroupement : « avec qui / dans quoi » un fichier a été partagé. */
export function documentContextKey(doc: DocumentRow, userId: string): string {
  switch (doc.scope) {
    case 'contact':
      return `contact:${otherPartyId(doc, userId) ?? 'inconnu'}`;
    case 'class':
      return `class:${doc.classId}`;
    case 'room':
      return `room:${doc.roomId}`;
    case 'discussion':
      return `discussion:${doc.discussionId}`;
  }
}

export function documentContextLabel(doc: DocumentRow, userId: string): string {
  switch (doc.scope) {
    case 'contact':
      return (doc.uploadedBy === userId ? doc.peerName : doc.uploaderName) ?? 'Contact';
    case 'class':
      return doc.className ?? 'Classe';
    case 'room': {
      if (doc.roomTitle) return doc.roomTitle;
      if (doc.roomStartsAt) {
        const date = new Date(doc.roomStartsAt).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
        });
        return `Réunion du ${date}`;
      }
      return 'Réunion';
    }
    case 'discussion':
      return doc.discussionTitle ?? 'Discussion';
  }
}

/** Filtre par portée puis par texte (nom du fichier, contexte, auteur) — insensible aux accents. */
export function filterDocuments(
  docs: readonly DocumentRow[],
  filter: { scope: DocumentScopeFilter; query: string },
  userId: string,
): DocumentRow[] {
  return docs.filter((doc) => {
    if (filter.scope !== 'all' && doc.scope !== filter.scope) return false;
    return matchesQuery(
      filter.query,
      doc.fileName,
      documentContextLabel(doc, userId),
      doc.uploaderName,
    );
  });
}

export interface DocumentGroup {
  key: string;
  scope: DocumentScope;
  label: string;
  documents: DocumentRow[];
  /** Date du fichier le plus récent — les groupes sont triés là-dessus. */
  latestAt: string;
}

/** Regroupe par contexte ; groupes et fichiers du plus récent au plus ancien. */
export function groupDocuments(docs: readonly DocumentRow[], userId: string): DocumentGroup[] {
  const groups = new Map<string, DocumentGroup>();
  for (const doc of docs) {
    const key = documentContextKey(doc, userId);
    const group = groups.get(key);
    if (group) {
      group.documents.push(doc);
      if (doc.createdAt > group.latestAt) group.latestAt = doc.createdAt;
    } else {
      groups.set(key, {
        key,
        scope: doc.scope,
        label: documentContextLabel(doc, userId),
        documents: [doc],
        latestAt: doc.createdAt,
      });
    }
  }
  const result = [...groups.values()];
  for (const group of result) {
    group.documents.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  return result.sort((a, b) => b.latestAt.localeCompare(a.latestAt));
}

/**
 * Miroir UI de `can_manage_document` (migration 0261) : l'auteur, ou l'HÔTE de
 * la réunion. Le tuteur de la classe est toujours l'auteur (seul à déposer) ;
 * un admin passe par la RLS. Le serveur reste la garde ultime.
 */
export function canDeleteDocument(doc: DocumentRow, userId: string, isRoomHost = false): boolean {
  if (doc.uploadedBy === userId) return true;
  return doc.scope === 'room' && isRoomHost;
}

/**
 * Ligne de méta-données d'un fichier : « 2 Mo · 28 sept. · Toi ». `context`
 * ajoute le « où » (contact, classe, réunion) quand la liste mélange plusieurs contextes.
 */
export function documentMeta(doc: DocumentRow, userId: string, context?: string): string {
  const date = new Date(doc.createdAt).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
  const author = doc.uploadedBy === userId ? 'Toi' : (doc.uploaderName ?? null);
  return [formatFileSize(doc.sizeBytes), date, author, context].filter(Boolean).join(' · ');
}
